import React, { useEffect, useState } from "react";
import { Input, Button, Alert, Spin, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { fetchMiscMode, saveMiscMode } from "../../models/shared/miscModeModel";

// Generic "manage a named, id-stable list of {value, label} types" editor,
// backed by a single uzd_misc mode. Modal-agnostic — fetches once on mount
// (callers that need a fresh fetch per open rely on their Modal's
// destroyOnHidden to remount this component each time), and renders its own
// inline Save (+ optional Cancel) button rather than a Modal-level footer,
// so it can be embedded either as one Modal's whole body (ManageTypesModal)
// or as one tab's content among several (ManageTypesTabsModal).
function safeNextValue(data, defaultNextValue) {
  if (typeof data?.nextValue === "number" && Number.isFinite(data.nextValue)) return data.nextValue;
  const types = Array.isArray(data?.types) ? data.types : [];
  return types.length ? Math.max(...types.map(t => t.value)) + 1 : defaultNextValue;
}

export function TypesEditor({ mode, title, defaultTypes = [], defaultNextValue = 0, onCancel, onSaved }) {
  const [types, setTypes] = useState([]);
  const [nextValue, setNextValue] = useState(defaultNextValue);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchMiscMode(mode, { force: true })
      .then(data => {
        const savedTypes = Array.isArray(data?.types) ? data.types : null;
        if (savedTypes && savedTypes.length > 0) {
          setTypes(savedTypes);
          setNextValue(safeNextValue(data, defaultNextValue));
        } else {
          setTypes(defaultTypes);
          setNextValue(defaultNextValue);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const updateLabel = (index, label) => {
    setTypes(prev => prev.map((t, i) => i === index ? { ...t, label } : t));
  };

  const removeType = (index) => {
    setTypes(prev => prev.filter((_, i) => i !== index));
  };

  const addType = () => {
    setTypes(prev => [...prev, { value: nextValue, label: "" }]);
    setNextValue(v => v + 1);
  };

  const canSave = types.length > 0 && types.every(t => t.label.trim().length > 0);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        nextValue,
        types: types.map(t => ({ value: t.value, label: t.label.trim() })),
      };
      const res = await saveMiscMode(mode, payload);
      if (!res.success) { setError(res.message || `Failed to save ${title.toLowerCase()}`); return; }
      message.success(`${title} saved.`);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
      {error && <Alert type="error" showIcon message={error} />}
      {types.map((t, i) => (
        <div key={t.value} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input
            placeholder="Type name"
            value={t.label}
            onChange={e => updateLabel(i, e.target.value)}
          />
          <Button icon={<DeleteOutlined />} aria-label="Delete" onClick={() => removeType(i)} />
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={addType} block>
        Add Type
      </Button>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button type="primary" disabled={!canSave} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
}
